import { getCities } from 'api/cities'
import { URL_PARSE_OPTIONS, URL_STRINGIFY_OPTIONS } from 'application/configuration/constants'
import { createEffect, createEvent, createStore } from 'effector'
import { appendUrl } from 'lib/append-url'
import { getFromStorage, setToStorage, storage } from 'lib/storage'
import { parseUrl } from 'query-string'

/**
 * Все города
 */
export const $cities = createStore<string[]>([])

/**
 * Активые (выбраные в данный момент) города
 */
export const $selectedCities = createStore<string[]>([])

/**
 * Запрос на города
 */
export const fetchCitiesFx = createEffect(getCities)

/**
 * Переключить город в выбранных городах
 */
export const setSelectedCities = createEvent<string[]>()

/**
 * Выбрать авто-определённый город
 */
export const useAutoDetectedCities = createEvent()
export const useGeoAutoDetectedCities = createEvent()
export const startLoad = createEvent()

/**
 * Хранит понимание о том, был ли текущий активный город авто-определён или нет
 */
export const $cityWasJustAutoDetected = createStore<boolean>(false)

/**
 * Вызвается когда пользователь принял авто-определённый город (согласился с выбором)
 */
export const acceptAutoDetectedCity = createEvent()

/**
 * Идёт ли загрузка городов в данный момент
 */
export const $fetching = createStore(true)

/**
 * Получили ли мы уже города из url, local storage или с помощью автоопределения
 */
export const $areCitiesSelected = createStore(false)

/**
 * Отписываемся от изменений города в шапке сайта
 */
export const unsubscribeChangesCitiesInHeader = createEvent()

/**
 * Подписываемся ли мы на событие изменения города в шапке или нет
 */
export const $subscribeChangesCitiesInHeader = createStore<boolean>(false)

export const subscribeChangesCitiesInHeader = (event: any) => {
  setSelectedCities([event.detail.city])
}
/**
 * Подписываемся на событие изменения города в шапке, только елси при первой загрузке не было города в url
 */
export const useSubscribeChangesCitiesInHeader = createEffect(() => {
  document.addEventListener('@kodix/one/locator-updated', subscribeChangesCitiesInHeader)
})

/** Получить города из УРЛ **/
export const getFromURLFx = createEffect(async (allCities: string[]) => {
  if (sessionStorage.getItem('showCarsNextDealer')) return []

  const cityFromStorage = getFromStorage('citySelectselectedCities')

  if (cityFromStorage) {
    return cityFromStorage
  }

  const citiesInUrl = parseUrl(window.location.href, URL_PARSE_OPTIONS).query.city

  if (!citiesInUrl) throw new Error('No cities found in URL')

  if (citiesInUrl) {
    const potentialCities = citiesInUrl instanceof Array ? citiesInUrl : [citiesInUrl]

    /** Если город найден в урл,
     * но не все найденые горрода могут быть выбраны,
     * полностью инвалидируем выбор и считаем, что попытка достать города из URL провалилась
     */
    if (!potentialCities.every((city) => allCities.includes(city))) {
      throw new Error(
        'Cities was found in URL but cannot be applied because not all saved cities are available for selection'
      )
    }

    return potentialCities
  } else throw new Error('No cities was found in URL')
})

/** Получить города из Локалстораджа **/
export const getFromStorageFx = createEffect(async () => {
  const cityFromStorage = getFromStorage('citySelectselectedCities')
  const cityInHeader = getFromStorage('@kodix/one/headerDealer/geolocation')

  if (!cityInHeader || !cityFromStorage) throw new Error('No cities found in localstorage ')
  return cityInHeader ? [cityInHeader] : [cityFromStorage]
})

/** Получить города из Локалстораджа, при этом,
 *  если город найден в локалстораджах,
 *  но не все найденые города могут быть выбраны,
 *  полностью инвалидируем выбор и считаем, что попытка достать города из локалстораджа провалилась
 */
export const getValidCityFromStorageFx = createEffect(async (allCities: string[]) => {
  const cities = await getFromStorageFx()

  if (!cities.every((city: string) => allCities.includes(city)))
    throw new Error(
      'Cities was found in localstorage but cannot be applied because not all saved cities are available for selection'
    )

  return cities
})

export const getNearest = createEffect((nearest: string) => {
  return nearest
})

/**
 * Записать выбраный город в УРЛ
 */
export const writeToURLFx = createEffect((cities: string[]) => {
  const url = appendUrl({
    url: window.location.href,
    parseOptions: URL_STRINGIFY_OPTIONS,
    stringifyOptions: URL_STRINGIFY_OPTIONS,
    data: { city: cities }
  })
  window.history.replaceState({}, '', url)
})

/**
 * Записываем выбраный город в локалсторадж
 */
export const writeToStorageFx = createEffect((cities: string[]) => {
  setToStorage('citySelectselectedCities', cities)
})
